/**
 * @fileoverview BudgetTrackerPage - Main transaction tracking page.
 *
 * The primary page of the application where users view and manage their monthly
 * transactions. Displays month navigation, balance information, category summaries,
 * transaction list, and transaction management modal.
 *
 * @description This page component:
 * - Displays month navigation with balance and close status
 * - Shows category spending summaries
 * - Lists all transactions for the selected month
 * - Provides transaction add/edit/delete functionality via modal
 * - Shows warnings when no categories exist
 *
 * Component composition:
 * - MonthNavigation: Month selector with balance display
 * - NoCategoriesWarning: Alert when categories need to be added
 * - TransactionModal: Add/edit transaction form
 * - CategorySummary: Spending breakdown by category
 * - TransactionList: Scrollable list of all transactions
 *
 * @module pages/BudgetTrackerPage
 */
import { useState } from 'react';
import MonthNavigation from '../components/MonthNavigation/MonthNavigation';
import CategorySummary from '../components/CategorySummary/CategorySummary';
import TransactionList from '../components/TransactionList/TransactionList';
import TransactionModal from '../components/TransactionModal/TransactionModal';
import EditTransactionModal from '../components/EditTransactionModal/EditTransactionModal';
import NoCategoriesWarning from '../components/shared/NoCategoriesWarning';
import CeasedBudgetBanner from '../components/shared/CeasedBudgetBanner';
import BankBalanceModal from '../components/BankBalance/BankBalanceModal';
import FloatingActionButton from '../components/shared/FloatingActionButton';
import ErrorBoundary from '../components/ErrorBoundary/ErrorBoundary';
import { useBankBalances } from '../hooks/useBankBalances';
import { budgetAPI } from '../services/api';
import styles from './BudgetTrackerPage.module.css';

/**
 * Main budget tracking page component.
 *
 * @param {Object} props - Component props
 * @param {Object} props.monthNav - Month navigation hook
 * @param {Object} props.balanceHook - Month balance hook
 * @param {Object} props.transactionHook - Transaction management hook
 * @param {Object} props.categoryHook - Category hook
 * @param {Object} props.categoryBalancesHook - Category balances hook
 * @param {Object} props.budgetInfo - Budget metadata
 * @param {Object} props.budgetHook - Budget template hook
 * @param {string} props.userRole - Current user's role
 * @param {number} props.userKey - Current user's key
 * @param {Function} props.onNavigateToCategories - Navigate to categories page
 */
function BudgetTrackerPage({
  monthNav,
  balanceHook,
  transactionHook,
  categoryHook,
  categoryBalancesHook,
  budgetInfo,
  _budgetHook,
  userRole,
  userKey,
  onNavigateToCategories
}) {
  const [isTransactionModalOpen, setIsTransactionModalOpen] = useState(false);
  const [isBankBalanceModalOpen, setIsBankBalanceModalOpen] = useState(false);

  // Bank balances hook
  const bankBalancesHook = useBankBalances(budgetInfo?.budgetKey, monthNav.selectedMonth);

  const canManageBankBalances = userRole === 'owner' || userRole === 'elevated_member';

  // Handler to restore budget (uncease)
  const handleRestoreBudget = async () => {
    try {
      await budgetAPI.unceaseBudget(budgetInfo.budgetKey);
      // Refresh the page to update budget info
      window.location.reload();
    } catch (error) {
      console.error('Failed to restore budget:', error);
      alert('Failed to restore budget. Please try again.');
    }
  };

  return (
    <div className={styles.page}>
      {/* Ceased Budget Banner */}
      {budgetInfo?.ceaseDate && (
        <CeasedBudgetBanner
          ceaseDate={budgetInfo.ceaseDate}
          isOwner={userRole === 'owner'}
          onRestore={handleRestoreBudget}
        />
      )}

      {/* Month navigation with balance display */}
      <div className={styles.contentSection}>
        <MonthNavigation
          selectedMonthDisplay={monthNav.getSelectedMonthDisplayName()}
          isCurrentMonth={monthNav.isViewingCurrentMonth()}
          transactionCount={transactionHook.filteredTransactions.length}
          totalTransactionCount={transactionHook.transactions.length}
          onPreviousMonth={() => monthNav.handleNavigateMonth('prev')}
          onNextMonth={() => monthNav.handleNavigateMonth('next')}
          onGoToCurrentMonth={monthNav.goToCurrentMonth}
          isPrevDisabled={monthNav.isPrevDisabled}
          isNextDisabled={monthNav.isNextDisabled}
          isClosed={monthNav.isClosed}
          isClosing={monthNav.isClosing}
          closeError={monthNav.closeError}
          onCloseMonth={monthNav.handleCloseMonth}
          onOpenMonth={monthNav.handleOpenMonth}
          endingBalance={balanceHook.endingBalance}
          startingBalance={balanceHook.startingBalance}
          currentBalance={balanceHook.currentBalance}
          balanceError={balanceHook.error}
          bankBalance={bankBalancesHook.totalBalance}
          onUpdateBankBalance={() => setIsBankBalanceModalOpen(true)}
        />
      </div>

      {/* Warning when no categories exist - only show after categories have loaded */}
      <NoCategoriesWarning
        categories={categoryHook.categories}
        isLoading={categoryHook.isLoading}
        hasLoadedOnce={categoryHook.hasLoadedOnce}
        onNavigateToCategories={onNavigateToCategories}
      />

      {/* Add transaction button - hide when budget is ceased */}
      {!budgetInfo?.ceaseDate && (
        <div className={styles.addTransactionSection}>
          <button
            onClick={() => setIsTransactionModalOpen(true)}
            className={styles.addTransactionButton}
          >
            Add Transaction
          </button>
        </div>
      )}

      {/* Transaction modal */}
      <TransactionModal
        isOpen={isTransactionModalOpen}
        onClose={() => {
          setIsTransactionModalOpen(false);
          transactionHook.resetForm();
          transactionHook.setErrors({});
        }}
        transactionHook={transactionHook}
        budgetStartMonth={budgetInfo?.startMonth}
        singleBankMode={budgetInfo?.singleBankMode || false}
      />

      {/* Edit transaction modal */}
      <EditTransactionModal
        isOpen={transactionHook.editingId !== null}
        onClose={() => transactionHook.cancelEdit()}
        transactionHook={transactionHook}
        budgetStartMonth={budgetInfo?.startMonth}
        singleBankMode={budgetInfo?.singleBankMode || false}
      />

      {/* Category spending summary */}
      <ErrorBoundary fallbackMessage="Unable to load category summary. Please refresh the page.">
        <div className={styles.contentSection}>
          <CategorySummary
            spendingByCategory={transactionHook.spendingByCategory}
            selectedMonth={monthNav.selectedMonth}
            categoryObjects={categoryHook.categories}
            categoryBalances={categoryBalancesHook.categoryBalances}
            getBalanceForCategory={categoryBalancesHook.getBalanceForCategory}
            singleBankMode={budgetInfo?.singleBankMode || false}
          />
        </div>
      </ErrorBoundary>

      {/* Transaction list */}
      <ErrorBoundary fallbackMessage="Unable to load transactions. Please refresh the page.">
        <div className={styles.contentSection}>
          <TransactionList
            transactions={transactionHook.filteredTransactions}
            transactionHook={transactionHook}
            userRole={userRole}
            userKey={userKey}
            readOnly={budgetInfo?.ceaseDate !== null}
          />
        </div>
      </ErrorBoundary>

      {/* Bank Balance Modal */}
      <BankBalanceModal
        isOpen={isBankBalanceModalOpen}
        onClose={() => setIsBankBalanceModalOpen(false)}
        balances={bankBalancesHook.balances}
        onDeleteBalance={bankBalancesHook.deleteBalance}
        onSaveBalances={bankBalancesHook.updateBalances}
        totalBalance={bankBalancesHook.totalBalance}
        onBalanceChange={bankBalancesHook.updateLocalBalance}
        readOnly={!canManageBankBalances || budgetInfo?.ceaseDate !== null}
      />

      {/* Floating action button for mobile - hide when any modal is open or budget is ceased */}
      {!budgetInfo?.ceaseDate && !isTransactionModalOpen && !isBankBalanceModalOpen && transactionHook.editingId === null && (
        <FloatingActionButton
          onClick={() => setIsTransactionModalOpen(true)}
          ariaLabel="Add transaction"
        />
      )}
    </div>
  );
}

export default BudgetTrackerPage;
